import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import queryString from 'query-string'
import { Download } from 'lucide-react'
import { Button, type ButtonProps } from '@vnetwork/datacentral-ui/primitives'
import { toast } from '@vnetwork/datacentral-ui/feedback'
import datacentral from '@/utils/axios/datacentral'
import { useModal } from '@/contexts/modal'
import type { ColumnMapping, ActiveFilter } from '@/components/ExcelExportModal'

export type { ColumnMapping }

export interface ExcelExportButtonProps {
    /** List endpoint; the export reuses it with `export=excel`. */
    apiEndpoint: string
    columns: ColumnMapping[]
    queryParams: Record<string, any>
    feature?: string
    buttonText?: string
    disabled?: boolean
    activeFilters?: ActiveFilter[]
    variant?: ButtonProps['variant']
    /** `md` in a table toolbar row, level with the search and Filters. */
    size?: ButtonProps['size']
    className?: string
}

/** Opens the column picker, then downloads the server-generated .xlsx. */
export function ExcelExportButton({
    apiEndpoint,
    columns,
    queryParams,
    feature,
    buttonText,
    disabled,
    activeFilters,
    variant = 'ghost',
    size = 'sm',
    className,
}: ExcelExportButtonProps) {
    const { t } = useTranslation('data-table')
    const { setModal } = useModal()
    const [isExporting, setIsExporting] = useState(false)

    const handleExport = useCallback(
        async (selectedColumns: ColumnMapping[], filename: string) => {
            try {
                setIsExporting(true)
                const params = {
                    ...queryParams,
                    export: 'excel',
                    columns: JSON.stringify(selectedColumns),
                    ...(filename && { filename: `${filename}.xlsx` }),
                }
                const response = await datacentral.get(
                    `/${apiEndpoint.replace(/\/+$/, '')}?${queryString.stringify(params)}`,
                    { responseType: 'blob' }
                )
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                })
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = filename ? `${filename}.xlsx` : 'export.xlsx'
                document.body.appendChild(link)
                link.click()
                window.URL.revokeObjectURL(url)
                document.body.removeChild(link)
                toast.success(t('export.success'))
            } catch (error) {
                console.error('Export error:', error)
                toast.error(t('export.error'))
            } finally {
                setIsExporting(false)
            }
        },
        [apiEndpoint, queryParams, t]
    )

    return (
        <Button
            variant={variant}
            size={size}
            className={className}
            loading={isExporting}
            disabled={disabled}
            startIcon={<Download size={size === 'sm' ? 14 : 16} />}
            onClick={() =>
                setModal('ExcelExport/index', {
                    onExport: handleExport,
                    availableColumns: columns,
                    feature,
                    activeFilters,
                })
            }
        >
            {isExporting
                ? t('export.exporting')
                : buttonText || t('export.excel')}
        </Button>
    )
}
